import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import * as R from 'Router/routes';

type Route = (typeof R)[keyof typeof R];

/** If offline PSD2, hide sidebar */
const isPSD2SidebarAvailable = !sessionStorage.getItem('psd2Token');

type RouteProgress = {
  step: number;
  section: number;
  withCalculator: boolean;
  showFooter: boolean;
  showSidebar: boolean;
  showProgress: boolean;
  disabled?: boolean;
  nextStepsDisabled?: boolean;
  layoutStyles?: string;
};

const stepRouteMapping: Partial<Record<Route, RouteProgress>> = {
  [R.WELCOME_BACK]: {
    step: 0,
    section: 2,
    withCalculator: false,
    showFooter: true,
    showSidebar: false,
    showProgress: true,
  },
  [R.WELCOME_BACK_NEW]: {
    step: 0,
    section: 0,
    withCalculator: false,
    showFooter: true,
    showSidebar: false,
    showProgress: true,
    disabled: true,
    layoutStyles: 'px-0 py-0',
  },
  [R.INTRO]: {
    step: 0,
    section: 0,
    withCalculator: true,
    showFooter: true,
    showSidebar: true,
    showProgress: true,
  },
  [R.INTRO_START_APP]: {
    step: 0,
    section: 0,
    withCalculator: true,
    showFooter: true,
    showSidebar: true,
    showProgress: true,
  },
  [R.INTRO_2]: {
    step: 0,
    section: 0,
    withCalculator: true,
    showFooter: true,
    showSidebar: true,
    showProgress: true,
  },
  [R.BANK_ID]: {
    step: 2,
    section: 2,
    withCalculator: false,
    showFooter: true,
    showSidebar: true,
    showProgress: true,
    layoutStyles: 'px-0 py-0',
  },
  [R.BANK_ID_ACCOUNT_SELECTION]: {
    step: 2,
    section: 2,
    withCalculator: false,
    showFooter: true,
    showSidebar: true,
    showProgress: true,
    layoutStyles: 'px-0',
  },
  [R.BANK_ID_CONFIRM]: {
    step: 2,
    section: 2,
    withCalculator: false,
    showFooter: true,
    showSidebar: true,
    showProgress: true,
    layoutStyles: 'px-0 py-0',
  },
  [R.BANK_ID_ERROR]: {
    step: 2,
    section: 0,
    withCalculator: false,
    showFooter: true,
    showSidebar: false,
    showProgress: true,
  },
  [R.BANK_ID_WAIT]: {
    step: 16,
    section: 2,
    withCalculator: false,
    showFooter: false,
    showSidebar: false,
    showProgress: false,
    layoutStyles: 'max-w-none justify-center',
  },
  [R.PERSONAL_DETAILS]: {
    step: 3,
    section: 1,
    withCalculator: true,
    showFooter: true,
    showSidebar: true,
    showProgress: true,
    layoutStyles: 'px-0',
  },
  [R.PERSONAL_DETAILS_CONTINUE]: {
    step: 3,
    section: 1,
    withCalculator: false,
    showFooter: false,
    showSidebar: false,
    showProgress: false,
    layoutStyles: 'max-w-none justify-center',
  },
  [R.PHONE_VERIFICATION]: {
    step: 4,
    section: 1,
    withCalculator: false,
    showFooter: true,
    showSidebar: true,
    showProgress: true,
  },
  [R.PHONE_CHANGE]: {
    step: 4,
    section: 0,
    withCalculator: false,
    showFooter: true,
    showSidebar: true,
    showProgress: true,
  },
  [R.FINANCE]: {
    step: 3,
    section: 1,
    withCalculator: false,
    showFooter: true,
    showSidebar: true,
    showProgress: true,
    layoutStyles: 'max-w-none',
  },
  [R.REJECTED_OFFER]: {
    disabled: true,
    step: 5,
    section: 2,
    withCalculator: false,
    showFooter: true,
    showSidebar: false,
    showProgress: true,
  },
  [R.DOWNSALE]: {
    step: 5,
    section: 3,
    withCalculator: false,
    showFooter: true,
    showSidebar: true,
    showProgress: true,
    layoutStyles: 'px-0',
  },
  [R.UPSALE]: {
    step: 5,
    section: 3,
    withCalculator: false,
    showFooter: true,
    showSidebar: false,
    showProgress: true,
    layoutStyles: 'px-0',
  },
  [R.UPSALE_CHANGE_AMOUNT]: {
    step: 6,
    section: 3,
    withCalculator: false,
    showFooter: true,
    showSidebar: false,
    showProgress: true,
    layoutStyles: 'px-0 bg-primary-foreground lg:bg-background-accent',
  },
  [R.CONSENT]: {
    step: 7,
    section: 3,
    withCalculator: false,
    showFooter: true,
    showSidebar: true,
    showProgress: true,
    layoutStyles: 'px-0',
  },
  [R.SIGN_AGREEMENT]: {
    step: 7,
    section: 3,
    withCalculator: false,
    showFooter: true,
    showSidebar: true,
    showProgress: true,
    layoutStyles: 'px-0',
  },
  [R.MANUAL_OCR]: {
    step: 7,
    section: 2,
    withCalculator: false,
    showFooter: true,
    showSidebar: true,
    showProgress: true,
    layoutStyles: 'px-0',
  },
  [R.OCR_RESULTS]: {
    step: 8,
    section: 2,
    withCalculator: false,
    showFooter: true,
    showSidebar: true,
    showProgress: true,
  },
  [R.MANUAL_UPLOAD_ID]: {
    step: 8,
    section: 2,
    withCalculator: false,
    showFooter: true,
    showSidebar: true,
    showProgress: true,
    layoutStyles: 'px-0',
  },
  [R.INCOME_PROOF]: {
    step: 9,
    section: 2,
    withCalculator: false,
    showFooter: true,
    showSidebar: true,
    showProgress: true,
  },
  [R.MANUAL_IDENTIFICATION]: {
    step: 10,
    section: 2,
    withCalculator: false,
    showFooter: true,
    showSidebar: true,
    showProgress: true,
  },
  [R.ADJUST_INCOME]: {
    step: 9,
    section: 2,
    withCalculator: false,
    showFooter: true,
    showSidebar: true,
    showProgress: true,
  },
  [R.PSD2_BANKS]: {
    step: 10,
    section: 2,
    withCalculator: false,
    showFooter: true,
    showSidebar: isPSD2SidebarAvailable,
    showProgress: isPSD2SidebarAvailable,
  },
  [R.OFFLINE_PSD2_BANKS]: {
    step: 10,
    section: 2,
    withCalculator: false,
    showFooter: true,
    showSidebar: isPSD2SidebarAvailable,
    showProgress: isPSD2SidebarAvailable,
  },
  [R.PSD2_GUIDE]: {
    step: 11,
    section: 2,
    withCalculator: false,
    showFooter: true,
    showSidebar: isPSD2SidebarAvailable,
    showProgress: isPSD2SidebarAvailable,
  },
  [R.PSD2_CONSENTS]: {
    step: 12,
    section: 2,
    withCalculator: false,
    showFooter: true,
    showSidebar: isPSD2SidebarAvailable,
    showProgress: isPSD2SidebarAvailable,
    layoutStyles: 'px-0',
  },
  [R.PSD2_BANK_ACCOUNT_SELECT]: {
    step: 13,
    section: 2,
    withCalculator: false,
    showFooter: true,
    showSidebar: true,
    showProgress: true,
  },
  [R.PSD2_RECEIVING]: {
    step: 14,
    section: 2,
    withCalculator: false,
    showFooter: true,
    showSidebar: true,
    showProgress: true,
  },
  [R.PSD2_VERIFIED]: {
    step: 15,
    section: 3,
    withCalculator: false,
    showFooter: true,
    showSidebar: true,
    showProgress: true,
    nextStepsDisabled: true,
  },
  [R.WAITING]: {
    step: 16,
    section: 2,
    withCalculator: false,
    showFooter: false,
    showSidebar: false,
    showProgress: false,
    layoutStyles: 'max-w-none justify-center',
  },
  [R.PSD2_ERROR]: {
    step: 16,
    section: 2,
    withCalculator: false,
    showFooter: true,
    showSidebar: true,
    showProgress: false,
    layoutStyles: 'p-0',
  },
  [R.OFFLINE_PSD2_ERROR]: {
    step: 16,
    section: 2,
    withCalculator: false,
    showFooter: true,
    showSidebar: true,
    showProgress: false,
  },
  [R.DOCUMENT_UPLOAD_LATER]: {
    step: 16,
    section: 2,
    withCalculator: false,
    showFooter: true,
    showSidebar: true,
    showProgress: false,
  },
  [R.MANUAL_REVIEW]: {
    disabled: true,
    step: 16,
    section: 2,
    withCalculator: false,
    showFooter: true,
    showSidebar: false,
    showProgress: true,
  },
  [R.APPROVED]: {
    step: 17,
    section: 4,
    withCalculator: false,
    showFooter: true,
    showSidebar: false,
    showProgress: true,
  },
  [R.PENDING]: {
    step: 16,
    section: 2,
    withCalculator: false,
    showFooter: true,
    showSidebar: true,
    showProgress: true,
  },
  [R.MANUAL_PSD2_GUIDE]: {
    step: 18,
    section: 2,
    withCalculator: false,
    showFooter: true,
    showSidebar: true,
    showProgress: true,
  },
  [R.MANUAL_PSD2_CONSENTS]: {
    step: 19,
    section: 2,
    withCalculator: false,
    showFooter: true,
    showSidebar: isPSD2SidebarAvailable,
    showProgress: isPSD2SidebarAvailable,
  },
  [R.OFFLINE_PSD2_CONSENTS]: {
    step: 20,
    section: 2,
    withCalculator: false,
    showFooter: true,
    showSidebar: isPSD2SidebarAvailable,
    showProgress: isPSD2SidebarAvailable,
  },
  [R.MANUAL_PSD2_BANKS]: {
    step: 21,
    section: 2,
    withCalculator: false,
    showSidebar: true,
    showFooter: true,
    showProgress: true,
    layoutStyles: 'px-0',
  },
  [R.OFFLINE_PSD2_RECEIVING]: {
    step: 22,
    section: 2,
    withCalculator: false,
    showSidebar: true,
    showFooter: true,
    showProgress: true,
    layoutStyles: 'px-0',
  },
};

/** Tracks registration step and section progress */
const useProgress = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const sectionList = useMemo(
    () => [
      t('progress:intro'),
      t('progress:personalData'),
      t('progress:verification'),
      t('progress:summary'),
    ],
    [t],
  );

  const progress = useMemo(() => {
    let pathname: keyof typeof stepRouteMapping = location.pathname.replace(
      /\/+$/,
      '',
    ) as keyof typeof stepRouteMapping;

    const routeHasTokenOr404 = !Object.values(R).includes(pathname);
    if (routeHasTokenOr404) {
      // Remove last url element (token or provider data)
      pathname = location.pathname
        .split('/')
        .slice(0, -1)
        .join('/') as keyof typeof stepRouteMapping;
    }

    const {
      disabled,
      section,
      step,
      withCalculator,
      showFooter,
      showSidebar,
      showProgress,
      nextStepsDisabled,
      layoutStyles,
    } = stepRouteMapping[pathname] || {
      disabled: false,
      step: -1,
      section: -1,
      withCalculator: false,
      showFooter: false,
      showSidebar: false,
      showProgress: false,
      nextStepsDisabled: false,
      layoutStyles: '',
    };
    return {
      disabled,
      section,
      step,
      withCalculator,
      showFooter,
      showSidebar,
      showProgress,
      nextStepsDisabled,
      layoutStyles,
    };
  }, [location.pathname]);

  return useMemo(() => {
    return {
      sectionList,
      disabled: progress.disabled,
      step: progress.step,
      section: progress.section,
      withCalculator: progress.withCalculator,
      showFooter: progress.showFooter,
      showSidebar: progress.showSidebar,
      showProgress: progress.showProgress,
      nextStepsDisabled: progress.nextStepsDisabled,
      layoutStyles: progress.layoutStyles,
    };
  }, [progress, sectionList]);
};

export default useProgress;
